import { useContext, useEffect, useState } from "react";
import { t } from "i18next";
import { ArrowDown2, DocumentText, Layer } from "iconsax-react";
import DBButton from "../../../../component/global/DBButton";
import DBModal from "../../../../component/global/DBModal";
import DBSpinner from "../../../../component/global/DBSpinner";
import { ToastContext } from "../../../../component/global/ToastContext";
import { useRequest } from "../../../../hooks/helperData/useRequest";
import { SETTING_TEMPLATE_PREVIEW } from "../../../../helperData/ApiAddress";

const CRITERIA_TYPE_LABELS = {
    1: "ai.experience.criteria.type.1",
    2: "ai.experience.criteria.type.2",
    4: "ai.experience.criteria.type.4",
    5: "ai.experience.criteria.type.5",
};

function countTree(nodes) {
    return nodes.reduce(
        (total, node) => total + 1 + countTree(node.children || []),
        0
    );
}

function PreviewNode({ node }) {
    const [open, setOpen] = useState(false);
    const hasDetails =
        node.children?.length > 0 ||
        node.features?.length > 0 ||
        node.forms?.length > 0;

    return (
        <li className="preview-node">
            <button
                type="button"
                className={`preview-node-header ${open ? "open" : ""}`}
                onClick={() => hasDetails && setOpen(!open)}
                aria-expanded={open}
            >
                <Layer size={18} aria-hidden="true" />
                <span className="preview-node-name">{node.name}</span>
                <span className={`ai-type ai-type-${node.typ}`}>
                    {node.typ === 1
                        ? t("item.type.service")
                        : t("item.type.item")}
                </span>
                {node.forms?.length > 0 && (
                    <span className="preview-node-badge">
                        {t("template.preview.forms")}{" "}
                        {node.forms.length}
                    </span>
                )}
                {hasDetails && (
                    <ArrowDown2
                        size={16}
                        aria-hidden="true"
                        className="preview-node-chevron"
                    />
                )}
            </button>
            {open && (
                <div className="preview-node-body">
                    {node.features?.length > 0 && (
                        <div className="preview-section">
                            <h4>{t("template.preview.features")}</h4>
                            <div className="preview-chips">
                                {node.features.map((feature, index) => (
                                    <span key={index} className="preview-chip">
                                        {feature.name}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {node.forms?.map((form, index) => (
                        <div key={index} className="preview-form-card">
                            <div className="preview-form-header">
                                <DocumentText size={16} aria-hidden="true" />
                                <span>{form.name}</span>
                                <span className="preview-node-badge">
                                    {t("template.preview.criteria.count", {
                                        count: form.criteria?.length || 0,
                                    })}
                                </span>
                            </div>
                            <ul className="preview-criteria">
                                {form.criteria?.map((criterion, cIndex) => (
                                    <li key={cIndex}>
                                        <span>{criterion.name}</span>
                                        <span className="preview-chip muted">
                                            {t(
                                                CRITERIA_TYPE_LABELS[
                                                    criterion.criteria_state_type
                                                ] ||
                                                    "ai.experience.criteria.type.2"
                                            )}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                    {node.children?.length > 0 && (
                        <ul className="preview-tree">
                            {node.children.map((child, index) => (
                                <PreviewNode key={index} node={child} />
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </li>
    );
}

export default function TemplatePreviewModal({
    templateId,
    onHide,
    onApply,
    applying,
}) {
    const toast = useContext(ToastContext);
    const [getPreview, previewLoader] = useRequest(
        SETTING_TEMPLATE_PREVIEW,
        "GET"
    );
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (!templateId) {
            setPreview(null);
            return;
        }
        getPreview(null, null, `${templateId}/preview`)
            .then(setPreview)
            .catch(() => {
                toast(t("template.preview.error"), "danger");
                onHide();
            });
    }, [templateId]);

    const isEmpty =
        preview && (!preview.tree || preview.tree.length === 0);

    return (
        <DBModal
            show={Boolean(templateId)}
            onHide={onHide}
            minWidth={720}
            className="template-preview-modal"
        >
            <DBModal.Header
                closeBtn={onHide}
                title={
                    preview
                        ? `${t("template.preview.title")} — ${preview.name}`
                        : t("template.preview.title")
                }
            />
            <DBModal.Content>
                {previewLoader || !preview ? (
                    <div className="preview-loading" aria-live="polite">
                        <DBSpinner />
                        <p>{t("template.preview.loading")}</p>
                    </div>
                ) : isEmpty ? (
                    <p className="preview-empty">
                        {t("template.preview.empty")}
                    </p>
                ) : (
                    <>
                        {preview.description && (
                            <p className="preview-description">
                                {preview.description}
                            </p>
                        )}
                        <p className="preview-summary">
                            {t("ai.experience.confirm.groups", {
                                count: countTree(preview.tree),
                            })}
                        </p>
                        <ul className="preview-tree">
                            {preview.tree.map((node, index) => (
                                <PreviewNode key={index} node={node} />
                            ))}
                        </ul>
                    </>
                )}
            </DBModal.Content>
            <DBModal.Footer>
                <DBButton
                    caption={t("template.apply")}
                    onClick={() => onApply(templateId)}
                    loading={applying}
                    disabled={!preview || isEmpty}
                />
                <DBButton
                    caption={t("template.preview.close")}
                    type="secondary"
                    onClick={onHide}
                />
            </DBModal.Footer>
        </DBModal>
    );
}
